<template>
  <div class="group relative aspect-[160/230] overflow-hidden">
    <!-- IMAGE -->
    <AppImage
      v-if="entry.image"
      loading="lazy"
      class="size-full object-cover transition-transform duration-500 ease-in-out group-hover:scale-105"
      :src="entry.image"
    />

    <!-- TEXT -->
    <div
      class="absolute inset-0 z-10 flex flex-col justify-end p-4 text-left text-beige-500 sm:p-5"
    >
      <!-- Category -->
      <div
        v-if="
          entry.category.title.toLowerCase() != 'mitarbeiter' &&
          entry.category.title.toLowerCase() != 'staff'
        "
        class="mb-2 uppercase typo-subtitle-md sm:mb-4"
      >
        {{ entry.category.title }}
      </div>

      <!-- Title & Link -->
      <AppLink
        class="mb-4 flex items-center justify-between gap-4 after:absolute after:inset-0 after:z-10"
        :class="isPartnerOrConsultant ? 'cursor-pointer' : 'cursor-default'"
        v-bind="isPartnerOrConsultant ? { to: entry.url } : null"
        :aria-label="entry.title"
        aria-haspopup
        :aria-expanded="open"
        :aria-controls="`collapseText-${entry.id}`"
        @mouseenter="enter()"
        @mouseleave="leave()"
        @focus="enter()"
        @blur="leave()"
      >
        <span
          class="text-left text-beige-500 typo-heading-xs lg:typo-heading-sm"
        >
          {{ entry.title }}
        </span>
        <IconArrowRight
          v-if="isPartnerOrConsultant"
          class="size-5 shrink-0 self-end text-beige-500 transition-[opacity,transform] duration-500 group-hover:translate-x-3 group-hover:opacity-0 sm:mb-1 sm:size-7"
        />
      </AppLink>

      <!-- Competencies -->
      <div
        :id="`collapseText-${entry.id}`"
        ref="collapseElement"
        :aria-hidden="!open"
        class="typo-body-sm"
      >
        <p
          v-if="entry.competencies"
          class="s max-sm:text-s line-clamp-[5] pr-[10%] xl:line-clamp-[10]"
          v-html="entry.competencies"
        />

        <!-- EMAIL -->
        <a
          v-if="entry.email"
          :href="`mailto:${entry.email}`"
          class="relative z-20 mt-2 flex gap-x-1 hyphens-auto break-words text-beige-500 transition-colors hover:text-beige-600"
          @mouseenter="enter()"
          @focus="enter()"
          @blur="leave()"
        >
          <IconMail class="mt-1 size-5 shrink-0" />
          {{ entry.email }}
        </a>
      </div>
    </div>

    <!-- GRADIENT -->
    <div class="gradient pointer-events-none absolute inset-0" />

    <!-- GRADIENT HOVER -->
    <div
      class="gradient-hover pointer-events-none absolute inset-0 m-[-5%] origin-bottom blur-lg transition-[opacity,transform] duration-500 ease-in-out"
      :class="open ? 'scale-y-100 opacity-100' : 'scale-y-0 opacity-0'"
    />
  </div>
</template>

<script setup lang="ts">
import type { AssetFragment } from '#gql'

const props = defineProps<{
  entry: {
    id: string
    title: string
    url: string
    competencies: string
    phone: string
    email: string
    image: AssetFragment
    category: {
      id: string
      title: string
      slug: string
    }
  }
}>()

const collapseElement = ref()
const options = {
  duration: 300,
  initiallyClosed: true,
}
const open = useCollapse(collapseElement, options)

// https://gsap.com/docs/v3/Plugins/ScrollTrigger/static.isTouch/
const { isTouch } = useScrollTrigger

const leaveTimeout = ref()

const isPartnerOrConsultant = computed(() => {
  return (
    props.entry.category?.slug === 'partner' ||
    props.entry.category?.slug === 'konsulent'
  )
})

function enter() {
  if (isTouch === 1) {
    return
  }
  clearTimeout(leaveTimeout.value)

  open.value = true
}

function leave() {
  leaveTimeout.value = setTimeout(() => {
    open.value = false
  }, 200) // Adjust delay as needed for smoother transitions
}

open.value = false
</script>

<style scoped lang="scss">
.gradient {
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 50%,
    rgba(48, 61, 92, 0.8) 90%
  );
}

.gradient-hover {
  background: linear-gradient(
    180deg,
    rgba(10, 54, 169, 0.3) 0%,
    rgba(10, 54, 169, 1) 100%
  );
}
</style>
